// /settings — Business Settings Hub (PAGES.md §3.76)
//
// Central settings hub. Business info form + navigation cards to sub-pages.
// SHARED vertical. OWNER+ (50). Traceability: FR-TEN-001, FR-TEN-006

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Input,
  TextArea,
  Select,
  Toggle,
  FileUpload,
  Skeleton,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { SignOutAllDevicesCard } from '@/components/features/settings/SignOutAllDevicesCard';
import { tenantService } from '@/services/tenant.service';
import type { Tenant } from '@/types/tenant';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { uploadTenantLogo } from './logo-upload';
import { useUnsavedChangesGuard, guardedLeave } from '@/hooks/use-unsaved-changes-guard';
import { SaveStatus } from '@/components/SaveStatus';
import { STATE_OPTIONS } from '@/constants/indian-states';
import { GstSetupBanner } from '@/components/features/settings/GstSetupBanner';
import { deriveGstRegistered, gstinStateMismatch, gstSetupWarning } from '@/lib/gst-setup';

// ─── Constants ─────────────────────────────────────────────────────────────

const PHONE_REGEX = /^\+[1-9]\d{6,14}$/;
const FSSAI_REGEX = /^\d{14}$/;

// ─── Icons ─────────────────────────────────────────────────────────────────

function SettingsIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="h-4 w-4 text-neutral-400 dark:text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function TaxIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 5L5 19M9 7a2 2 0 11-4 0 2 2 0 014 0zm10 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function BellIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function PaletteIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z" />
      <circle cx="7.5" cy="11.5" r="1.5" /><circle cx="12" cy="7.5" r="1.5" /><circle cx="16.5" cy="11.5" r="1.5" />
    </svg>
  );
}

function CardIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M2 10h20M6 14h4" />
    </svg>
  );
}

function SmsIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6h16a2 2 0 012 2v8a2 2 0 01-2 2h-8l-5 4v-4H4a2 2 0 01-2-2V8a2 2 0 012-2z" />
      <path d="M7 11h.01M11 11h.01M15 11h.01" />
    </svg>
  );
}

function EmailIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2z" />
      <path d="M3 8l9 7 9-7" />
    </svg>
  );
}

function WhatsAppIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14c.75 1.25 2.25 2.5 4 2.5 2 0 3.5-1 4.25-2.5" />
      <path d="M8 10c.75-1.25 2.25-2.5 4-2.5 2 0 3.5 1 4.25 2.5" />
      <path d="M5 16l1-3" />
      <path d="M5 16l-2 3 3.5-1" />
    </svg>
  );
}

// ─── Nav Card ───────────────────────────────────────────────────────────────

function NavCard({ icon, title, description, href, roleBadge }: {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
  roleBadge?: string;
}) {
  const navigate = useNavigate();
  return (
    <div
      className={cn(GLASS_INTERACTIVE, 'p-5 flex items-center gap-4 animate-slide-up')}
      // Navigates in code, which the unsaved-changes link guard can't see.
      onClick={() => guardedLeave(() => navigate(href))}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') guardedLeave(() => navigate(href)); }}
    >
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white flex-shrink-0 shadow-sm">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{title}</p>
          {roleBadge && (
            <span className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">{roleBadge}</span>
          )}
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{description}</p>
      </div>
      <ChevronRightIcon />
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function BusinessSettingsContent() {
  const dial = useTenantDial();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const tenantId = profile?.tenant_id ?? '';
  const currency = useTenantCurrency();
  // Statutory tax-id vocabulary/validation derives from the immutable currency.
  // INR → GST_INDIA: label `GSTIN`, GSTIN regex (byte-identical to before),
  // stored in `gstin`. GCC → `TRN`/`VATIN`/… + that country's own pattern,
  // stored in `tax_registration_number`; Qatar/Kuwait have no statutory id
  // (`taxIdLabel === null`) ⇒ the field is hidden entirely.
  const tax = useTaxRegime();
  const isIndia = tax.regime === 'GST_INDIA';
  const isRestaurant = profile?.tenant?.business_type === 'RESTAURANT';

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [businessName, setBusinessName] = useState('');
  const [gstin, setGstin] = useState('');
  const [fssaiLicense, setFssaiLicense] = useState('');
  const [address, setAddress] = useState('');
  // India only (gst-parked task 030): the business State — the GST supplier
  // state when no GSTIN says otherwise — and the "GST registered" choice
  // (null = worked out from the GSTIN).
  const [bizState, setBizState] = useState('');
  const [gstSetting, setGstSetting] = useState<boolean | null>(null);
  // What the server holds, for the banner and to send gst_registered only
  // when the owner changed it (an untouched switch stays "worked out").
  const [savedGst, setSavedGst] = useState<{ setting: boolean | null; gstin: string; state: string }>({
    setting: null, gstin: '', state: '',
  });
  const [phone, setPhone] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const initialRef = useRef<string>('');

  useEffect(() => { document.title = 'Settings — Orio360 Admin'; }, []);

  // Fetch tenant
  useEffect(() => {
    if (!tenantId) return;
    // A superseded load (effect re-run, StrictMode double mount) must not land late
    // and overwrite what the user has typed since — nor reset the unsaved baseline.
    let cancelled = false;
    setIsLoading(true);
    tenantService.getById(tenantId).then((data) => {
      if (cancelled) return;
      // The baseline holds exactly what the fields hold (null → ''), or a
      // freshly loaded form with an empty field would read as unsaved.
      const loaded = {
        business_name: data.business_name ?? '',
        gstin: (isIndia ? data.gstin : data.tax_registration_number) ?? '',
        address: data.address ?? '',
        state: data.state ?? '',
        gst_registered: data.gst_registered_setting ?? null,
        phone: data.phone ?? '',
        logo_url: data.logo_url ?? '',
        fssai_license: data.fssai_license ?? '',
      };
      setTenant(data);
      setBusinessName(loaded.business_name);
      setGstin(loaded.gstin);
      setAddress(loaded.address);
      setBizState(loaded.state);
      setGstSetting(loaded.gst_registered);
      setSavedGst({ setting: loaded.gst_registered, gstin: data.gstin ?? '', state: loaded.state });
      setPhone(loaded.phone);
      setLogoUrl(loaded.logo_url);
      setFssaiLicense(loaded.fssai_license);
      initialRef.current = JSON.stringify(loaded);
    }).catch(() => {
      if (!cancelled) toast({ title: 'Failed to load settings', variant: 'error' });
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [tenantId, isIndia, toast]);

  const isDirty =
    !!initialRef.current &&
    JSON.stringify({
      business_name: businessName,
      gstin,
      address,
      state: bizState,
      gst_registered: gstSetting,
      phone,
      logo_url: logoUrl,
      fssai_license: fssaiLicense,
    }) !== initialRef.current;

  const gstWarning = gstSetupWarning({
    isIndia,
    setting: savedGst.setting,
    gstin: savedGst.gstin,
    state: savedGst.state,
    locations: profile?.locations ?? [],
  });

  const handleLogoUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) {
        setLogoUrl('');
        return;
      }

      const file = files[0];
      setIsLogoUploading(true);

      try {
        const fileUrl = await uploadTenantLogo(file);

        setLogoUrl(fileUrl);
        // The upload only populates local form state — nothing is persisted to the
        // tenant until Save Settings. Say so, so the toast doesn't read as a
        // completed, saved action (issue #273).
        toast({ title: 'Logo ready — click Save Settings to apply', variant: 'success' });
      } catch {
        toast({ title: 'Logo upload failed', variant: 'error' });
      } finally {
        setIsLogoUploading(false);
      }
    },
    [toast],
  );

  const handleSave = useCallback(async () => {
    if (!tenantId) return;
    if (gstin && tax.taxIdRegex && !tax.taxIdRegex.test(gstin.toUpperCase())) {
      toast({ title: `Invalid ${tax.taxIdLabel ?? 'tax ID'} format`, variant: 'error' }); return;
    }
    if (phone && !PHONE_REGEX.test(phone)) {
      toast({ title: 'Invalid phone number', variant: 'error' }); return;
    }
    if (isRestaurant && fssaiLicense && !FSSAI_REGEX.test(fssaiLicense)) {
      toast({ title: 'FSSAI must be 14 digits', variant: 'error' }); return;
    }
    // A GSTIN's first two digits are its state. Only when one of the two
    // changed, so older data never blocks an unrelated edit (the API agrees).
    const mismatch = isIndia && (gstin !== savedGst.gstin || bizState !== savedGst.state)
      ? gstinStateMismatch(gstin, bizState)
      : null;
    if (mismatch) {
      toast({ title: mismatch, variant: 'error' }); return;
    }

    setIsSaving(true);
    try {
      // One authorized write for the whole form. The name used to go through
      // `tenantService.update` (PATCH /tenants/:id), which is ADMIN-only — so an
      // Owner's save died there before the profile ever went out, discarding the
      // tax id, address and a freshly uploaded logo with it. The profile
      // endpoint now carries the name too, which also removes the half-saved
      // state the two writes could leave behind (#272).
      // An emptied field is sent as null so the API clears the saved id —
      // `undefined` is dropped by JSON and left the old one in place (GP4-001).
      const taxIdValue = gstin.toUpperCase().trim() || null;
      const saved = await tenantService.updateProfile(tenantId, {
        business_name: businessName.trim(),
        // India → `gstin`; GCC → `tax_registration_number` (the API rejects a
        // GCC id in `gstin`, which only accepts the GSTIN pattern).
        ...(isIndia ? { gstin: taxIdValue } : { tax_registration_number: taxIdValue }),
        address: address.trim() || undefined,
        // India only — the State and the GST registered switch are hidden for
        // VAT businesses. The switch is sent only when the owner changed it.
        ...(isIndia && bizState ? { state: bizState } : {}),
        ...(isIndia && gstSetting !== savedGst.setting ? { gst_registered: gstSetting } : {}),
        phone: phone || undefined,
        logo_url: logoUrl || undefined,
        // Restaurant-only; the field is hidden (and never sent) elsewhere.
        ...(isRestaurant && fssaiLicense ? { fssai_license: fssaiLicense.trim() } : {}),
      });
      toast({ title: 'Business settings updated', variant: 'success' });
      initialRef.current = JSON.stringify({
        business_name: businessName,
        gstin,
        address,
        state: bizState,
        gst_registered: gstSetting,
        phone,
        logo_url: logoUrl,
        fssai_license: fssaiLicense,
      });
      if (isIndia) {
        const savedGstin = saved?.gstin ?? gstin;
        const savedState = saved?.state ?? bizState;
        setSavedGst({ setting: gstSetting, gstin: savedGstin, state: savedState });
        // The dashboard banner reads the signed-in profile; bring it up to
        // date without another request. Best-effort — the next /auth/me load
        // carries it anyway, and the save itself already landed.
        try {
          useAuthStore.setState((st) => (st.profile?.tenant ? {
            profile: {
              ...st.profile,
              tenant: {
                ...st.profile.tenant,
                gstin: savedGstin || null,
                state: savedState || null,
                gst_registered_setting: gstSetting,
                gst_registered: saved?.gst_registered ?? deriveGstRegistered(gstSetting, savedGstin),
              },
            },
          } : {}));
        } catch {
          // non-fatal
        }
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to save';
      // A single write either lands or it doesn't — nothing to reconcile.
      toast({ title: message, variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [tenantId, businessName, gstin, address, bizState, gstSetting, savedGst, phone, logoUrl, fssaiLicense, isRestaurant, tax, isIndia, toast]);

  // Keyboard: Ctrl+S — mirror the Save button's guard (no-op when pristine or already saving)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        if (!isDirty || isSaving) return;
        handleSave();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, isDirty, isSaving]);

  // Unsaved-changes guard: warn before the tab is closed or refreshed, or an
  // in-app link is followed, while the form (including a just-uploaded logo
  // that has not been persisted yet) is dirty, so the work isn't lost silently (#273).
  const { dialog: leaveDialog } = useUnsavedChangesGuard(isDirty);

  return (
    <PageContainer
      title="Settings"
      subtitle="Manage your business configuration"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Settings' }]}
    >
      <div className="animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(99,102,241,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-white/5 blur-3xl" />
          <div className="relative flex items-center gap-3">
            <SettingsIcon className="h-7 w-7 text-neutral-300" />
            <div>
              <h1 className="text-2xl font-bold text-white">Business Settings</h1>
              <p className="text-neutral-300 mt-1">Configure your business identity, tax, notifications, and branding</p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton variant="rect" height={300} className="rounded-2xl" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => <Skeleton key={i} variant="rect" height={100} className="rounded-2xl" />)}
            </div>
          </div>
        ) : (
          <>
            <GstSetupBanner warning={gstWarning} canFix onSettingsPage />

            {/* Business Info Form */}
            <div className={cn(GLASS_FORM, 'p-5 sm:p-6 mb-6 animate-slide-up')}>
              <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-4">
                Business Information
              </h2>
              <div className="space-y-4 max-w-2xl">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2 block">
                    Business Logo
                  </label>
                  <FileUpload
                    variant="dropzone"
                    accept="image/png,image/jpeg,image/webp"
                    maxSize={5 * 1024 * 1024}
                    preview={logoUrl || null}
                    onUpload={handleLogoUpload}
                    loading={isLogoUploading}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Business Name *</label>
                    <Input
                      value={businessName}
                      onChange={setBusinessName}
                      placeholder="Your Business Name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Business Type</label>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={tenant?.business_type === 'RESTAURANT' ? 'warning' : 'success'} size="sm">
                        {tenant?.business_type ?? 'RETAIL'}
                      </Badge>
                      <span className="text-[10px] text-neutral-400 dark:text-neutral-500">Immutable</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Statutory tax id — `GSTIN` (India) / `TRN`·`VATIN`·… (GCC).
                      Qatar/Kuwait have no statutory id ⇒ field hidden. */}
                  {tax.taxIdLabel && (
                    <div>
                      <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">{tax.taxIdLabel}</label>
                      <Input
                        value={gstin}
                        onChange={(value) => setGstin(value.toUpperCase())}
                        placeholder={isIndia ? 'e.g. 27AAPCT1234F1Z5' : `e.g. ${tax.taxIdExample ?? ''}`}
                        className="font-mono tracking-wide"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Currency</label>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{currency} ({getCurrencySymbol(currency)})</span>
                      <span className="text-[10px] text-neutral-400 dark:text-neutral-500">Read-only</span>
                    </div>
                  </div>
                </div>

                {/* India GST only (gst-parked task 030): the business State and
                    whether the business is GST registered. */}
                {isIndia && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="business-state" className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">State</label>
                      <Select
                        id="business-state"
                        options={STATE_OPTIONS}
                        value={bizState}
                        onChange={(v) => setBizState(typeof v === 'string' ? v : '')}
                        placeholder="Pick your State"
                      />
                      <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">
                        It must match the first two digits of your GSTIN.
                      </p>
                    </div>
                    <div>
                      <Toggle
                        checked={gstSetting ?? deriveGstRegistered(null, gstin)}
                        onChange={setGstSetting}
                        label="GST registered"
                        description={
                          gstSetting === null
                            ? 'Worked out from your GSTIN. It is on when a GSTIN is set.'
                            : 'You set this yourself.'
                        }
                        className="w-full"
                      />
                      {gstSetting !== null && (
                        <button
                          type="button"
                          onClick={() => setGstSetting(null)}
                          className="text-[11px] font-medium text-brand-600 dark:text-brand-400 hover:underline mt-1 transition-all duration-200"
                        >
                          Work it out from my GSTIN
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {isRestaurant && (
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                      FSSAI License Number
                    </label>
                    <Input
                      value={fssaiLicense}
                      onChange={setFssaiLicense}
                      placeholder="14-digit FSSAI number"
                      aria-describedby="fssai-hint"
                    />
                    <p id="fssai-hint" className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">
                      14-digit FSSAI license number (required for restaurant businesses)
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Phone</label>
                  <Input
                    variant="phone"
                    defaultDial={dial}
                    value={phone}
                    onChange={setPhone}
                    placeholder="98765 43210"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Address</label>
                  <TextArea
                    value={address}
                    onChange={setAddress}
                    placeholder="Business address..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <SaveStatus dirty={isDirty} />
                  <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
                    {isSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Sub-page Navigation Cards */}
            <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-3 px-1">
              Configuration
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <NavCard
                icon={<TaxIcon />}
                title="Tax Configuration"
                description={
                  tax.splitLabels
                    ? 'GST slabs and pricing mode'
                    : `${tax.taxName} rate and pricing mode`
                }
                href="/settings/tax"
                roleBadge="OWNER"
              />
              <NavCard
                icon={<TaxIcon />}
                title="Cash Rounding"
                description="Round cash totals to the coins you use"
                href="/settings/cash-rounding"
                roleBadge="OWNER"
              />
              <NavCard
                icon={<BellIcon />}
                title="Notifications"
                description="Email, SMS, WhatsApp, push preferences"
                href="/settings/notifications"
                roleBadge="MANAGER+"
              />
              <NavCard
                icon={<PaletteIcon />}
                title="Branding"
                description="Logo, colors, receipt header & footer"
                href="/settings/branding"
                roleBadge="OWNER"
              />
            </div>

            {/* Integrations — BYO provider credentials */}
            <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-3 px-1">
              Integrations
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <NavCard
                icon={<CardIcon />}
                title="Payment Gateway"
                description="Razorpay API keys, webhook secret, liveness probe"
                href="/settings/payment-gateway"
                roleBadge="OWNER"
              />
              <NavCard
                icon={<SmsIcon />}
                title="SMS Gateway"
                description="Twilio credentials, DLT sender ID, liveness probe"
                href="/settings/sms"
                roleBadge="OWNER"
              />
              <NavCard
                icon={<EmailIcon />}
                title="Email Delivery"
                description="Gmail or custom SMTP, verified sender, health alerts"
                href="/settings/email"
                roleBadge="OWNER"
              />
              <NavCard
                icon={<WhatsAppIcon />}
                title="WhatsApp"
                description="Meta Business Cloud API credentials, webhook setup, liveness probe"
                href="/settings/whatsapp"
                roleBadge="OWNER"
              />
            </div>

            {/* Security — account-level actions for the signed-in user */}
            <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-3 px-1">
              Security
            </h2>
            <div className="mb-6">
              <SignOutAllDevicesCard />
            </div>
          </>
        )}
      </div>
      {leaveDialog}
    </PageContainer>
  );
}

export function BusinessSettingsPage() {
  return (
    <ErrorBoundary>
      <BusinessSettingsContent />
    </ErrorBoundary>
  );
}
